import type { Features, NavItem, ToolOverride } from "@atomic/app-shell";
import { ChartNoAxesCombined, GlassWater, Layers, Map, Sparkles, Warehouse } from "lucide-react";

/** What LMNT tells the shell's rail: the live features, the Engine tiles and
 *  the tile overrides. One module so main.tsx and routeRegistry.test.ts build
 *  the same nav — the registry the assistant routes on is derived from it. */

/** Data, Reporting, Digital Twin and Nucleus are live. Tracker, Allocation and
 *  Linear stay off the rail (the shell's default for them) — LMNT reaches
 *  /trackers, /allocation-engine and /linear by direct navigation, through its
 *  own routes. */
export const FEATURES: Features = {
  masterdata: true,
  reporting: true,
  digitalTwin: true,
  nucleus: true,
};

/** The Replenishment page split three ways (LMN experiment): Supply is its
 *  Balance Walk alone; Sparkling and Drink Mix are its Production + Transfers
 *  scoped to one product class each, with independently locked plans.
 *  /replenishment itself stays routed — links and bookmarks keep working — it
 *  just has no tile. ADMIN/ROOT only, which needs no rule here: the shell
 *  hides the Engine group from a DATA_USER wholesale and admits one to Data
 *  and Reporting and nothing else. MRP opens on the Material Plan; POs to Place
 *  and the BOM Explorer are its other live views (containers/MrpView/tabs.ts). */
export const NAV_EXTRAS: NavItem[] = [
  { href: "/demand", name: "Demand", icon: ChartNoAxesCombined, group: "engine", aliases: ["demand reporting", "forecast"], description: "Demand reporting and sales-forecast views." },
  { href: "/supply", name: "Supply", icon: Warehouse, group: "engine", aliases: ["supply", "balance walk page"], description: "The Replenishment Balance Walk on its own page." },
  { href: "/sparkling", name: "Sparkling", icon: Sparkles, group: "engine", aliases: ["sparkling planning"], description: "Production + Transfers planning for the Sparkling class." },
  { href: "/drink-mix", name: "Drink Mix", icon: GlassWater, group: "engine", aliases: ["drink mix planning"], description: "Production + Transfers planning for the Drink Mix class." },
  { href: "/mrp", name: "MRP", icon: Layers, group: "engine", aliases: ["mrp", "materials", "material plan", "material requirements"], description: "Material requirements planning. Opens on the Material Plan (per site and material, week by week, from the selected MRP run). POs to Place lists the run's recommended buys with order-by dates; the BOM Explorer is also live. Data Gaps is not yet live." },
  // An Engine module, after the planning modules (Jeff, 2026-10-29): the default order
  // lives in core (app-shell `FEATURE_PLACEMENT`, keyed by the feature), which places it
  // whatever group is written here. A switchable Nucleus FEATURE (`network_design`): the
  // gateway's src/nucleus/feature_policy.json says who holds it — ROOT alone today — and
  // the shell shows the tile only when `whoAmI.nucleusFeatures` lists it (app-shell
  // `holdsNavItem`). The gateway refuses the relays on the same policy.
  {
    href: "/network-design",
    name: "Network Design",
    icon: Map,
    group: "engine",
    feature: "network_design",
    aliases: ["network design", "outbound network", "network explorer", "network map", "warehouse map"],
    description:
      "Outbound network explorer: demand by destination on a map, mapped to each warehouse / 3PL by proximity against its capacity, with a scenario mode that adds locations and remaps volume live.",
  },
];

/** No Docs tile: the page stays routed, it is only off the rail. */
export const TOOL_OVERRIDES: ToolOverride[] = [{ href: "/docs", hidden: true }];
